import { translateBackend } from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ButtonBase, Icon } from '@mui/material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { EditTab } from '@ovComponents/5-page/pageConfiguration/components/editTab';
import {
  type ClientPageInterface,
  PageObjectType,
  type TabInterface,
} from '@ovComponents/5-page/pageConfiguration/pageConfigurationTypes';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { useGlobalToast } from '@providers/globalToastProvider';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { usePermissions } from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { safeWindowOpen } from '@utils/urlSanitization';
import { useContext, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DroppableProvided,
  type DropResult,
} from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { useTabNavTokens } from './tabNav.tokens';

export type LocalTabInterface = {
  label: string;
  icon?: string | null;
  translatedUrl?: TranslatedString;
  key?: string;
};

export const NewTab = ({
  icon,
  label,
  isActive = false,
  i,
  translatedUrl,
  setActiveTab,
  isPageConfigurationView,
  page,
  setPage,
  testId = 'tab',
  badge,
}: {
  icon?: string | null;
  label: string;
  i: number;
  isActive?: boolean;
  translatedUrl?: TranslatedString;
  setActiveTab: (tab: any) => void;
  isPageConfigurationView?: boolean;
  page?: ClientPageInterface;
  setPage?: (page: any) => void;
  testId?: string;
  badge?: string;
}) => {
  const { showToast } = useGlobalToast();
  const { permissions } = usePermissions();
  const [hover, setHover] = useState(false);
  const { comp } = useThemeTokens(useTabNavTokens());

  return (
    <Box sx={{ position: 'relative', mr: 1.5 }}>
      <ButtonBase
        data-testid={testId}
        sx={{
          justifyContent: 'center',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row',
          color: isActive ? comp.tabNav.text.active : comp.tabNav.text.default,
          borderRadius: comp.tabNav.borderRadius,
          backgroundColor: isActive
            ? comp.tabNav.background.active
            : comp.tabNav.background.default,
          height: comp.tabNav.height,
          width: comp.tabNav.width,
          whiteSpace: comp.tabNav.width === 'auto' ? 'nowrap' : 'normal',
          padding: comp.tabNav.padding,
          '&:hover': { color: comp.tabNav.text.hover, cursor: 'pointer' },
          '&:focus': {
            backgroundColor: comp.tabNav.background.hover,
            color: comp.tabNav.text.hover,
            cursor: 'pointer',
          },
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={() => {
          if (translateBackend(translatedUrl)) {
            safeWindowOpen(translateBackend(translatedUrl), {
              target: '_blank',
              onError: (errorMsg) => {
                showToast({ severity: 'error', message: errorMsg });
              },
            });
            if (isPageConfigurationView) setActiveTab(i);
          } else {
            setActiveTab(i);
          }
        }}
      >
        {icon && (
          <Icon sx={{ fontSize: '14px !important', mr: 0.5 }}>{icon}</Icon>
        )}
        <Typography variant='bodySmall' weight='bold'>
          {label}
        </Typography>
        {translateBackend(translatedUrl) && hover && (
          <OpenInNewIcon sx={{ fontSize: '14px', ml: 1 }} />
        )}
        {isPageConfigurationView &&
          page &&
          setPage &&
          permissions.includes('write:page_configurations') && (
            <EditTab
              page={page}
              activeTab={i}
              setPage={setPage}
              hover={hover}
              resetHoverState={() => setHover(false)}
            />
          )}
      </ButtonBase>
      {badge && (
        <Box
          sx={{
            position: 'absolute',
            top: -3,
            right: -10,
            pointerEvents: 'none',
          }}
        >
          <Badge
            label={badge}
            variant='number'
            testId={`${testId}-badge`}
            sx={{
              minWidth: '12px',
              minHeight: '12px',
              fontSize: '9px',
              padding: '1px 3px',
              lineHeight: 1,
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export const TabNav = ({
  tabs,
  activeTab,
  setActiveTab,
  isPageConfigurationView,
  page,
  setPage,
  objectType,
}: {
  tabs: TabInterface[];
  activeTab: number;
  setActiveTab: (tab: number) => void;
  isPageConfigurationView?: boolean;
  page?: ClientPageInterface;
  setPage?: (page: any) => void;
  objectType?: PageObjectType;
}) => {
  const { sendAnalytic } = useContext(AnalyticsContext);
  const pageObjectTypeAnalyticsName: Map<PageObjectType, string> = new Map([
    [PageObjectType.INDIVIDUAL, 'clients_profile'],
    [PageObjectType.GOAL, 'clients_goal_profile'],
    [PageObjectType.SUB_ACCOUNT, 'clients_subAccount_profile'],
    [PageObjectType.ACCOUNT, 'clients_account_profile'],
    [PageObjectType.NON_INDIVIDUAL, 'clients_non_individual_profile'],
    [PageObjectType.HOUSEHOLD, 'clients_household_profile'],
    [PageObjectType.ACCOUNT_GROUP, 'clients_account_group_profile'],
  ]);

  const handleAnalytics = (name: string): void => {
    if (objectType) {
      const prefix = pageObjectTypeAnalyticsName.get(objectType);
      if (prefix) sendAnalytic(ovAnalyticsEvents.tabSelect(prefix, name));
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'start',
        flexDirection: 'row',
        mt: 3,
        mb: 2,
        overflow: 'auto',
        width: '100%',
      }}
    >
      {tabs.map((tab, index) => (
        <NewTab
          testId={`tab-${index}`}
          key={index}
          icon={tab.icon}
          label={translateBackend(tab.label)}
          isActive={index === activeTab}
          i={index}
          translatedUrl={tab.translatedUrl}
          setActiveTab={(indexOfActiveTab) => {
            // i18n: Using .en for analytics tracking - must be consistent across all users
            handleAnalytics((tab.label.en ?? '').toLowerCase());
            setActiveTab(indexOfActiveTab);
          }}
          isPageConfigurationView={isPageConfigurationView}
          page={page}
          setPage={setPage}
        />
      ))}
    </Box>
  );
};

export const DraggableTabNav = ({
  tabs,
  activeTab,
  setActiveTab,
  isPageConfigurationView,
  page,
  setPage,
  objectType,
}: {
  tabs: TabInterface[];
  activeTab: number;
  setActiveTab: (tab: number) => void;
  isPageConfigurationView?: boolean;
  page?: ClientPageInterface;
  setPage?: (page: any) => void;
  objectType?: PageObjectType;
}) => {
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { t } = useTranslation('pageConfiguration');
  const { sys } = useThemeTokens();
  const pageObjectTypeAnalyticsName: Map<PageObjectType, string> = new Map([
    [PageObjectType.INDIVIDUAL, 'clients_profile'],
    [PageObjectType.GOAL, 'clients_goal_profile'],
    [PageObjectType.SUB_ACCOUNT, 'clients_subAccount_profile'],
    [PageObjectType.ACCOUNT, 'clients_account_profile'],
    [PageObjectType.NON_INDIVIDUAL, 'clients_non_individual_profile'],
    [PageObjectType.HOUSEHOLD, 'clients_household_profile'],
  ]);

  const handleAnalytics = (name: string): void => {
    if (objectType) {
      const prefix = pageObjectTypeAnalyticsName.get(objectType);
      if (prefix) sendAnalytic(ovAnalyticsEvents.tabSelect(prefix, name));
    }
  };

  const onDragEnd = (result: DropResult) => {
    const newColumns = [...tabs];
    if (!result.destination) {
      return;
    }
    const item = newColumns[result.source.index];
    newColumns.splice(result.source.index, 1);
    newColumns.splice(result.destination.index, 0, item);
    setPage && setPage({ ...page, tabs: newColumns });
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId={'test'} direction='horizontal'>
        {(provided: DroppableProvided) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            style={{
              background: sys.color.background,
              display: 'flex',
              marginTop: '24px',
              marginBottom: '16px',
              overflow: 'auto',
              width: '100%',
            }}
          >
            {/* i18n: Using .en for React keys and draggableId - must be stable for reconciliation */}
            {tabs.map((tab, index) => (
              <Draggable
                key={tab.label.en}
                draggableId={`${tab.label.en}-${JSON.stringify(tab.widgets)}`}
                index={index}
              >
                {(dragProvided) => (
                  <div
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    {...dragProvided.dragHandleProps}
                  >
                    <NewTab
                      testId={`tab-${index}`}
                      key={index}
                      icon={tab.icon}
                      label={translateBackend(tab.label)}
                      isActive={index === activeTab}
                      i={index}
                      translatedUrl={tab.translatedUrl}
                      setActiveTab={(indexOfActiveTab) => {
                        // i18n: Using .en for analytics tracking - must be consistent across all users
                        handleAnalytics((tab.label.en ?? '').toLowerCase());
                        setActiveTab(indexOfActiveTab);
                      }}
                      isPageConfigurationView={isPageConfigurationView}
                      page={page}
                      setPage={setPage}
                      badge={tab.isHidden ? t('hidden').slice(0, 1) : undefined}
                    />
                  </div>
                )}
              </Draggable>
            ))}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export const SecondaryTab = ({
  icon,
  label,
  isActive = false,
  i,
  translatedUrl,
  setActiveTab,
  testId = 'tab',
  id,
}: {
  icon?: string | null;
  label: string;
  i: number;
  isActive?: boolean;
  translatedUrl?: TranslatedString;
  setActiveTab: (tab: any) => void;
  testId?: string;
  id?: string | number;
}) => {
  const { showToast } = useGlobalToast();
  const [hover, setHover] = useState(false);
  const { comp } = useThemeTokens(useTabNavTokens());

  return (
    <ButtonBase
      data-testid={testId}
      sx={{
        justifyContent: 'center',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        color: isActive ? comp.tabNav.text.active : comp.tabNav.text.default,
        borderRadius: comp.tabNav.borderRadius,
        backgroundColor: isActive
          ? comp.tabNav.background.active
          : comp.tabNav.background.default,
        height: comp.tabNav.height,
        width: comp.tabNav.width,
        whiteSpace: comp.tabNav.width === 'auto' ? 'nowrap' : 'normal',
        padding: comp.tabNav.padding,
        mr: 1.5,
        '&:hover': { color: comp.tabNav.text.hover, cursor: 'pointer' },
        '&:focus': {
          backgroundColor: comp.tabNav.background.hover,
          color: comp.tabNav.text.hover,
          cursor: 'pointer',
        },
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => {
        if (translateBackend(translatedUrl)) {
          safeWindowOpen(translateBackend(translatedUrl), {
            target: '_blank',
            onError: (errorMsg) => {
              showToast({ severity: 'error', message: errorMsg });
            },
          });
        } else {
          setActiveTab(id ?? i);
        }
      }}
    >
      {icon && (
        <Icon sx={{ fontSize: '14px !important', mr: 0.5 }}>{icon}</Icon>
      )}
      <Typography variant='bodySmall' weight='bold'>
        {label}
      </Typography>
      {translateBackend(translatedUrl) && hover && (
        <OpenInNewIcon sx={{ fontSize: '14px', ml: 1 }} />
      )}
    </ButtonBase>
  );
};

export const SecondaryTabs = ({
  tabs,
  activeTab,
  setActiveTab,
}: {
  tabs: LocalTabInterface[];
  activeTab: number | string;
  setActiveTab: (tab: number | string) => void;
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'start',
      flexDirection: 'row',
      mt: 3,
      mb: 2,
      overflow: 'auto',
      width: '100%',
    }}
  >
    {tabs.map((tab, index) => (
      <SecondaryTab
        testId={`tab-${index}`}
        id={tab.key ?? index}
        icon={tab.icon}
        label={tab.label}
        isActive={tab.key === activeTab || index === activeTab}
        i={index}
        translatedUrl={tab.translatedUrl}
        setActiveTab={setActiveTab}
      />
    ))}
  </Box>
);
